import { useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";

/**
 * Custom hook for reliable screen size detection
 * Provides both Material-UI breakpoint detection and window width fallback
 */
export const useScreenSize = () => {
  // Material-UI breakpoint detection
  const isSmallScreenMUI = useMediaQuery((theme) =>
    theme.breakpoints.down("sm")
  );
  const isMediumScreenMUI = useMediaQuery((theme) =>
    theme.breakpoints.down("md")
  );
  const isLargeScreenMUI = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  // Window width state
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== "undefined") {
      // Get the actual device width, accounting for high DPI screens
      const actualWidth = window.innerWidth;
      const screenWidth = window.screen?.width || actualWidth;
      const devicePixelRatio = window.devicePixelRatio || 1;

      // For mobile devices, try to get the real viewport width
      // Check if we're on a mobile device first
      const isMobileUA =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobileUA && screenWidth > 0) {
        // For mobile devices, use screen width if it's more reasonable
        if (screenWidth <= 480 && actualWidth > screenWidth * 1.5) {
          return screenWidth;
        }
      }

      // If the innerWidth is significantly larger than screen width,
      // it might be due to zoom or viewport issues
      if (screenWidth > 0 && actualWidth > screenWidth * 1.5) {
        return screenWidth;
      }

      return actualWidth;
    }
    return 1200; // Default fallback
  });

  // Additional mobile detection
  const [isMobileDevice, setIsMobileDevice] = useState(() => {
    if (typeof window !== "undefined") {
      // Check user agent
      const userAgentMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Check if it's a touch device
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      // Check if viewport is mobile-sized (even if window.innerWidth is large due to zoom)
      const actualWidth = window.innerWidth;
      const screenWidth = window.screen?.width || actualWidth;
      const isMobileViewport =
        actualWidth <= 480 ||
        screenWidth <= 480 ||
        (screenWidth > 0 &&
          actualWidth > screenWidth * 1.5 &&
          screenWidth <= 480);

      return userAgentMobile || (isTouchDevice && isMobileViewport);
    }
    return false;
  });

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      // Get the actual device width, accounting for high DPI screens
      const actualWidth = window.innerWidth;
      const screenWidth = window.screen?.width || actualWidth;
      const devicePixelRatio = window.devicePixelRatio || 1;

      // For mobile devices, try to get the real viewport width
      const isMobileUA =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobileUA && screenWidth > 0) {
        // For mobile devices, use screen width if it's more reasonable
        if (screenWidth <= 480 && actualWidth > screenWidth * 1.5) {
          setWindowWidth(screenWidth);
          return;
        }
      }

      // If the innerWidth is significantly larger than screen width,
      // it might be due to zoom or viewport issues
      if (screenWidth > 0 && actualWidth > screenWidth * 1.5) {
        setWindowWidth(screenWidth);
      } else {
        setWindowWidth(actualWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fallback detection using window width
  // Use more appropriate breakpoints for mobile devices
  const isSmallScreenFallback = windowWidth < 768; // More appropriate for mobile
  const isMediumScreenFallback = windowWidth < 1024; // Tablet range
  const isLargeScreenFallback = windowWidth >= 1024; // Desktop

  // Enhanced mobile detection - if it's a mobile device, treat as small screen
  // For iPhone 12 Pro: 980px is likely due to zoom or viewport issues, so we need to be more aggressive
  const isMobileSmallScreen =
    isMobileDevice ||
    windowWidth < 480 ||
    (windowWidth < 1000 && isMobileDevice); // Account for zoom issues
  const isMobileMediumScreen =
    isMobileDevice && windowWidth >= 480 && windowWidth < 768;
  const isMobileLargeScreen = !isMobileDevice && windowWidth >= 1024;

  // Use Material-UI detection if available, otherwise use fallback
  // For mobile devices, prioritize mobile detection
  const isSmallScreen =
    isMobileSmallScreen ||
    (isSmallScreenMUI !== undefined ? isSmallScreenMUI : isSmallScreenFallback);
  const isMediumScreen =
    isMobileMediumScreen ||
    (isMediumScreenMUI !== undefined
      ? isMediumScreenMUI
      : isMediumScreenFallback);
  const isLargeScreen =
    isMobileLargeScreen ||
    (isLargeScreenMUI !== undefined ? isLargeScreenMUI : isLargeScreenFallback);

  // Debug information
  const debugInfo = {
    windowWidth,
    actualInnerWidth: typeof window !== "undefined" ? window.innerWidth : null,
    screenWidth: typeof window !== "undefined" ? window.screen?.width : null,
    devicePixelRatio:
      typeof window !== "undefined" ? window.devicePixelRatio : null,
    userAgent: typeof window !== "undefined" ? navigator.userAgent : null,
    isMobileDevice,
    isSmallScreenMUI,
    isMediumScreenMUI,
    isLargeScreenMUI,
    isSmallScreenFallback,
    isMediumScreenFallback,
    isLargeScreenFallback,
    isMobileSmallScreen,
    isMobileMediumScreen,
    isMobileLargeScreen,
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
  };

  return {
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    windowWidth,
    debugInfo,
  };
};
